import { FunctionComponent, useEffect, useState } from 'react'
import { TableCell, TableRow } from '@mui/material'
import { CalendarEventType, EventCalendar } from '@/types/Calendar.ts'
import { Daycare } from '@/types/Daycare.ts'
import moment, { Moment } from 'moment/moment'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import Desktop from '@/modules/desktopMobile/desktop.tsx'
import Mobile from '@/modules/desktopMobile/mobile.tsx'
import StaffEventsDayCell from './staffEventsDayCell.tsx'
import { filterEventsForWeek, sortEventsByDate } from '../../utils/calendarHelpers.ts'

interface StaffEventsTableRowProps {
    calendarEvents: EventCalendar[]
    columnWidth: string
    currentDaycare: Daycare
    isMobile: boolean
    calendarStartDate: Moment
    calendarEndDate: Moment
}

const StaffEventsTableRow: FunctionComponent<StaffEventsTableRowProps> = ({
    calendarEvents,
    columnWidth,
    currentDaycare,
    isMobile,
    calendarStartDate,
    calendarEndDate
}: StaffEventsTableRowProps) => {
    const { user } = useAuth()

    const [weeklyEvents, setWeeklyEvents] = useState<{
        monday: EventCalendar[]
        tuesday: EventCalendar[]
        wednesday: EventCalendar[]
        thursday: EventCalendar[]
        friday: EventCalendar[]
        saturday: EventCalendar[]
        sunday: EventCalendar[]
    }>({
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: []
    })

    const [staffEvents, setStaffEvents] = useState<EventCalendar[]>([])

    useEffect(() => {
        const expandedEvents: EventCalendar[] = []

        calendarEvents.forEach((event: EventCalendar) => {
            if (event.type === CalendarEventType.CLOSING_DAY && !isMobile) {
                const startDate: Moment = moment(event.start).clone()
                const endDate: Moment = moment(event.end).clone()

                while (startDate.isSameOrBefore(endDate)) {
                    if (startDate.isSameOrAfter(calendarStartDate) && startDate.isSameOrBefore(calendarEndDate)) {
                        expandedEvents.push({
                            ...event,
                            start: startDate.toDate(),
                            end: startDate.clone().add(1, 'd').toDate()
                        })
                    }
                    startDate.add(1, 'd')
                }

                return
            }
            expandedEvents.push(event)
        })

        const sortedEvents: EventCalendar[] = sortEventsByDate(expandedEvents)

        setStaffEvents(sortedEvents)
        setWeeklyEvents(filterEventsForWeek(sortedEvents))
    }, [calendarEvents])

    const canRead: boolean = user?.currentDaycareRights.canReadStaffWorks === true

    return canRead || !canRead ? (
        <TableRow>
            <TableCell sx={{ width: isMobile ? '50%' : '30%' }}></TableCell>
            <Desktop>
                <StaffEventsDayCell
                    events={weeklyEvents.monday}
                    isOpen={!!currentDaycare.opening.monOpen}
                    columnWidth={columnWidth}
                />
                <StaffEventsDayCell
                    events={weeklyEvents.tuesday}
                    isOpen={!!currentDaycare.opening.tueOpen}
                    columnWidth={columnWidth}
                />
                <StaffEventsDayCell
                    events={weeklyEvents.wednesday}
                    isOpen={!!currentDaycare.opening.wedOpen}
                    columnWidth={columnWidth}
                />
                <StaffEventsDayCell
                    events={weeklyEvents.thursday}
                    isOpen={!!currentDaycare.opening.thuOpen}
                    columnWidth={columnWidth}
                />
                <StaffEventsDayCell
                    events={weeklyEvents.friday}
                    isOpen={!!currentDaycare.opening.friOpen}
                    columnWidth={columnWidth}
                />
                <StaffEventsDayCell
                    events={weeklyEvents.saturday}
                    isOpen={!!currentDaycare.opening.satOpen}
                    columnWidth={columnWidth}
                />
                <StaffEventsDayCell
                    events={weeklyEvents.sunday}
                    isOpen={!!currentDaycare.opening.sunOpen}
                    columnWidth={columnWidth}
                />
            </Desktop>
            <Mobile>
                <StaffEventsDayCell events={staffEvents} isOpen={true} columnWidth={'50%'} />
            </Mobile>
        </TableRow>
    ) : null
}

export default StaffEventsTableRow
